import DashboardIcon from "@mui/icons-material/Dashboard";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

import InsertChartIcon from '@mui/icons-material/InsertChart';
import { Box } from "@mui/material";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import MainContent from "./components/MainContent/MainContent";
import { setTitle } from "./redux/actions/dashboardTitle";
import { Loader } from "./components/common/Loader";
import { ModuleRegistry } from "ag-grid-community";
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { getAccessToken } from "./utils/authUtils";
// LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-089652}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Multiple_Applications}_Developer_License_for_{20}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{31_August_2026}____[v3]_[01]_MTc4ODEzMDgwMDAwMA==cb959b9bfde93226378489360e0ff3d9");
ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)]);
LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-065772}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topline_Forecast}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topline_Forecast}_need_to_be_licensed___{Topline_Forecast}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_September_2025}____[v3]_[0102]_MTc1ODQwOTIwMDAwMA==372521b73ce277d3fad471863dd69994");
 
const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
 
          '&.Mui-focused .MuiOutlinedInput-notchedOutline ': {
            borderColor: '#000',  // Your desired border color
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            borderColor: '#000',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#000',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#000',
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          "&.Mui-selected": {
            backgroundColor: '#000',
          },
          "&.Mui-selected:hover": {
            backgroundColor: '#797979'
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#797979'
        },
      },
    },
 
  },
});
 
const getAccountName = (acct) =>
  acct?.name ||
  acct?.idTokenClaims?.name ||
  acct?.username ||
  acct?.idTokenClaims?.preferred_username ||
  acct?.idTokenClaims?.email ||
  localStorage.getItem("userName") ||
  "";
 
const getAccountEmail = (acct) =>
  acct?.username ||
  acct?.idTokenClaims?.email ||
  localStorage.getItem("data") ||
  "";
 
const buildUserDetailsUrl = (email) => {
  const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || "";
  const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const endpoint = cleanBase ? `${cleanBase}/api/v1/user/details` : "/api/v1/user/details";
  return `${endpoint}?userEmail=${encodeURIComponent(email)}`;
};
 
const buildFallbackUserDetails = (acct) => ({
  userName: getAccountName(acct) || localStorage.getItem("appUserName") || "",
  userEmail: getAccountEmail(acct) || localStorage.getItem("appUserEmail") || "",
  role: localStorage.getItem("appUserRole") || "",
});
 
function App() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const historyStack = useRef([location.pathname]);
  const skipPush = useRef(false);
  const handledRedirect = useRef(false);
  const lastUserNav = useRef(0);
  const userDetailsRequested = useRef("");
  const title = useSelector((state) => state?.dashboardDetails?.title);
  const tabDetails = useSelector((state) => state?.dashboardDetails?.tabDetails);
  const [menuItems, setMenuItems] = useState([]);
  const [userDetails, setUserDetails] = useState(() => ({
    userName: localStorage.getItem("appUserName") || localStorage.getItem("userName") || "",
    userEmail: localStorage.getItem("appUserEmail") || localStorage.getItem("data") || "",
    role: localStorage.getItem("appUserRole") || "",
  }));
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [riskMaterials, setRiskMaterials] = useState([])
  // Define public routes where we should NOT force an MSAL redirect
  const publicPaths = ["/login", "/dashboard"];
  const isPublic = publicPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(p)
  );
  const handleLogin = () => {
    const loginRequest = {
      // Basic delegated scopes for SPA. Do NOT use '/.default' in browser flows.
      scopes: ["openid", "profile", "email"],
    };
    instance.loginRedirect(loginRequest);
  };
 
  const applyUserDetails = (details) => {
    if (!details) return;
    setUserDetails(details);
    if (details.userName) localStorage.setItem("appUserName", details.userName);
    if (details.userEmail) localStorage.setItem("appUserEmail", details.userEmail);
    if (details.role) localStorage.setItem("appUserRole", details.role);
  };
 
  const loadUserDetails = async (acct) => {
    setUserDetailsLoading(true);
    // Always prefer the live MSAL account email over any cached value
    const liveEmail = getAccountEmail(acct);
    const cachedEmail = localStorage.getItem("appUserEmail");
    if (liveEmail && cachedEmail && cachedEmail !== liveEmail) {
      // A different user is now active — clear all stale cached identity
      localStorage.removeItem("appUserEmail");
      localStorage.removeItem("appUserName");
      localStorage.removeItem("appUserRole");
      localStorage.removeItem("dashboardFilters");
      localStorage.removeItem("SAPIBPTableFilters");
      localStorage.removeItem("data");
      localStorage.removeItem("userName");
    }
    const fallback = buildFallbackUserDetails(acct);
    const email = liveEmail || fallback.userEmail;
    try {
      if (!email) {
        applyUserDetails(fallback);
        return;
      }
      const accessToken = await getAccessToken(instance);
      if (!accessToken) {
        applyUserDetails(fallback);
        return;
      }
      const response = await fetch(buildUserDetailsUrl(email), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`User details request failed: ${response.status}`);
      }
      const data = await response.json();
      const roles = Array.isArray(data?.roles) ? data.roles.filter(Boolean) : [];
      const role = roles.join(", ");
      applyUserDetails({
        userName: data?.username || fallback.userName || email,
        userEmail: email,
        role: role || fallback.role,
      });
    } catch (error) {
      console.error("Failed to load user details", error);
      applyUserDetails(fallback);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const requestUserDetails = (acct) => {
    if (!acct) return;
    const email = getAccountEmail(acct);
    if (!email) {
      applyUserDetails(buildFallbackUserDetails(acct));
      return;
    }
    if (userDetailsRequested.current === email) return;
    userDetailsRequested.current = email;
    loadUserDetails(acct);
  };
 
  const processLogin = (response) => {
    const acct = response?.account;
    if (!acct) return;
    // Set active account so downstream components can read it reliably
    instance.setActiveAccount(acct);
    // Persist a robust display name fallback (matches other project)
    const name =
      acct?.name ||
      acct?.idTokenClaims?.name ||
      acct?.username ||
      acct?.idTokenClaims?.preferred_username ||
      acct?.idTokenClaims?.email || "";
    localStorage.setItem("data", acct?.username || "");
    localStorage.setItem("userName", name);
    if (response?.accessToken) {
      localStorage.setItem("accessToken", response.accessToken);
    }
    requestUserDetails(acct);
    // Restore intended path after login if present, but avoid overriding
    // a very recent user-initiated navigation.
    let post = "/dashboard";
    try {
      post = sessionStorage.getItem('postLoginPath') || '/dashboard';
      sessionStorage.removeItem('postLoginPath');
    } catch (e) {}
    if (Date.now() - (lastUserNav.current || 0) < 1500) {
      return;
    }
    if (post && post !== location.pathname) navigate(post);
  };
 
  // If user is already signed in, ensure they land on the dashboard
  useLayoutEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const acct = instance.getActiveAccount() || accounts?.[0];
    if (acct) requestUserDetails(acct);
  }, [accounts, inProgress]);

  useEffect(() => {
    if ((accounts && accounts.length > 0) || isAuthenticated) {
      // Only navigate when not already on dashboard to avoid loops
      if (!location.pathname || location.pathname === "/") {
        navigate("/dashboard");
      }
      const acct = instance.getActiveAccount() || accounts?.[0];
      if (acct) requestUserDetails(acct);
    }
  }, [accounts, isAuthenticated, location.pathname, navigate]);
 
  useEffect(() => {
    if (!localStorage.getItem("appUserRole")) {
      applyUserDetails(userDetails);
    }
  }, []);
 
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      instance
        .handleRedirectPromise()
        .then((response) => {
          if (response) {
            if (!handledRedirect.current) {
              handledRedirect.current = true;
              processLogin(response);
            }
          } else if ((!accounts || accounts.length === 0) && !isPublic) {
            // Only trigger interactive login when on non-public routes
            handleLogin();
          }
        })
        .catch((error) => console.error("Error during redirect:", error));
    }
  }, [instance, accounts, inProgress, isPublic]);
 
  // Maintain a lightweight navigation stack to handle browser back behavior.
  useEffect(() => {
    // Push location into our stack unless we flagged a programmatic navigation
    if (skipPush.current) {
      skipPush.current = false;
    } else {
      const last = historyStack.current[historyStack.current.length - 1];
      if (location.pathname !== last) historyStack.current.push(location.pathname);
    }
    // Debug: trace stack updates (removed in production)
  }, [location.pathname]);
 
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      // Debug: record popstate event and current stack (removed in production)
      // If pop navigates to root, try to recover a sensible previous path.
      // If none found, re-push the current state to prevent showing access-denied.
      if (path === '/') {
        if (historyStack.current.length > 1) {
          // search for the most recent non-landing/root entry
          let prevIndex = historyStack.current.length - 2;
          while (prevIndex >= 0 && historyStack.current[prevIndex] === '/') {
            prevIndex -= 1;
          }
          if (prevIndex >= 0) {
            const prev = historyStack.current[prevIndex];
            // shrink stack to that entry
            historyStack.current = historyStack.current.slice(0, prevIndex + 1);
              skipPush.current = true;
              // Debug: navigating to recovered previous path (removed in production)
              navigate(prev);
            return;
          }
        }
        // nothing sensible to go back to — re-push current URL to stay put
        // Debug: nothing sensible to go back to, re-pushing current URL (removed in production)
        window.history.pushState(null, "", window.location.href);
      } else {
        // normal back: remove top of our stack
        if (historyStack.current.length > 1) historyStack.current.pop();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [navigate]);
 
  useEffect(() => {
    if (tabDetails.title === "Landing") {
      setMenuItems(menuItems)
    }
  }, [tabDetails])
 
  const handleMenuItemClick = (path, label, item) => {
    // record user-initiated navigation to avoid auto-redirect races
    lastUserNav.current = Date.now();
    // Normalize path and navigate first, then set title
    const normalized = path && path.startsWith("/") ? path : `/${path}`;
    // Set the title in state first to avoid any re-render race
    // that could trigger redirects, then navigate.
    // Ensure we only dispatch serializable data (strip React elements like `icon`).
    const serializable = { ...item };
    try { delete serializable.icon; } catch (e) {}
    dispatch(setTitle(serializable));
    navigate(normalized);
  };
 
  const handleLogout = () => {
    localStorage.clear();
    navigate("/dashboard");
    instance.logoutRedirect();
  };

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const hasAccount = Boolean((accounts && accounts.length > 0) || instance.getActiveAccount());
    if (!hasAccount && !isAuthenticated) {
      localStorage.removeItem("dashboardFilters");
      localStorage.removeItem("SAPIBPTableFilters");
    }
  }, [accounts, inProgress, isAuthenticated, instance]);
 
  useEffect(() => {
    setMenuItems([
      {
        id: 1,
        path: "/dashboard",
        title: "Home",
      },
      {
        id: 2,
        path: "/dashboard",
        label: "Dashboard",
        icon: <DashboardIcon />,
        title: "Dashboard",
      },
   
        {
        id: 3,
        path: "/sap-ibp-sync-status",
        label: "SAP IBP Sync Status",
        icon: <StorageOutlinedIcon />,
        title: "SAP IBP Sync Status",
      },
     
      {
        id: 4,
        path: "/parameter-configuration",
        label: "Parameter Configuration",
        icon: <PermDataSettingIcon />,
        title: "Parameter Configuration",
      },
       {
        id: 5,
        path: "/sc-ticketsummary",
       label: "Ticket Summary",
       icon: <SummarizeIcon/>,
        title: "Ticket Summary",
       }
    ]);
  }, [tabDetails])
 
  useEffect(() => {
    if (!menuItems || !menuItems.length) return;
    const match = menuItems.find((item) => {
      if (!item?.path) return false;
      // Exact match first
      if (item.path === location.pathname) return true;
      // Support nested routes where location contains the item path
      return location.pathname.startsWith(item.path);
    });
    if (match) {
      const serializableMatch = { ...match };
      try { delete serializableMatch.icon; } catch (e) {}
      dispatch(setTitle(serializableMatch));
    }
  }, [location.pathname, menuItems, dispatch]);
 
 
  useLayoutEffect(() => {
    let animationFrameId;
 
    const measure = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
 
        if (height > 0) {
          setHeaderHeight(height);
        } else {
          animationFrameId = window.requestAnimationFrame(measure);
        }
      } else {
        animationFrameId = window.requestAnimationFrame(measure);
      }
    };
 
    animationFrameId = window.requestAnimationFrame(measure);
 
    window.addEventListener("resize", measure);
 
    return () => {
      window.removeEventListener("resize", measure);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [tabDetails]);
 
  return (
    (isPublic || (isAuthenticated && accounts?.[0])) ? <Box
      sx={{
        margin: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <ThemeProvider theme={theme}>
        <Header
          headerRef={headerRef}
          title={title}
          handleLogout={handleLogout}
          handleMenuItemClick={handleMenuItemClick}
          riskMaterials={riskMaterials}
          userDetails={userDetails}
        />
        <Box
          sx={{
            paddingTop: `${headerHeight}px`,
            transition: "padding-top 0.3s ease",
          }}
        >
          {userDetailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
              <Loader />
            </Box>
          ) : (
            <MainContent
              contentHeight={headerHeight}
              handleMenuItemClick={handleMenuItemClick}
              menuItems={menuItems}
              setRiskMaterials={setRiskMaterials}
            />
          )}
        </Box>
        <Footer />
        <ToastContainer />
      </ThemeProvider>
    </Box>
      :
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: "center" }}>
        <Loader />
      </div>
  );
}
 
export default App;